import { AppLayout } from '@/components/layouts/AppLayout'
import { DataTableRow } from '@/components/molecules/DataTableRow'
import { ListSearchInput, WorkshopSelect } from '@/components/molecules/ListFilters'
import { ListPagination } from '@/components/molecules/ListPagination'
import { SortableTableHead } from '@/components/molecules/SortableTableHead'
import { StatusBadges } from '@/components/molecules/StatusBadges'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useListParams } from '@/hooks/useListParams'
import { fetchScenarios } from '@/services/cases'
import type { EstimateFeedbackListQuery, WorkshopLocation } from '@backend/schemas'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'

type SortField = EstimateFeedbackListQuery['sortField']

const EstimateFeedbacks = () => {
  const navigate = useNavigate()
  const { params, update, setSort } = useListParams<SortField>({ sortField: 'updatedAt', sortDir: 'desc' })
  const query = { ...params, workshop: (params.workshop || undefined) as WorkshopLocation | undefined }

  const { data: feedbacks, isLoading } = useQuery({
    queryKey: ['scenarios', query],
    queryFn: () => fetchScenarios(query),
    placeholderData: keepPreviousData,
  })

  const sortProps = {
    sortField: params.sortField,
    sortDir: params.sortDir,
    onSort: (field: SortField) => setSort(field, field === 'regNr' ? 'asc' : 'desc'),
    disabled: !!params.search,
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Kalkylgranskaren</CardTitle>
            <div className="flex gap-4 mt-4">
              <ListSearchInput
                placeholder="Sök ärende (börjar med)..."
                value={params.search}
                onChange={search => update({ search })}
              />
              <WorkshopSelect value={params.workshop} onChange={workshop => update({ workshop })} />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTableHead label="Ärende" field="regNr" {...sortProps} />
                      <TableHead>Uppmaningar</TableHead>
                      <SortableTableHead label="Skapad" field="createdAt" {...sortProps} />
                      <SortableTableHead label="Senast uppdaterad" field="updatedAt" {...sortProps} />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {feedbacks?.items.length ? (
                      feedbacks.items.map(estimateFeedback => {
                        const summary = estimateFeedback.summary

                        return (
                          <DataTableRow
                            key={estimateFeedback.id}
                            onClick={() => navigate(`/estimate-feedback/${estimateFeedback.id}`)}
                          >
                            <TableCell className="font-medium">
                              {estimateFeedback.regNr}-{estimateFeedback.cabasCaseIndex}
                            </TableCell>
                            <TableCell>
                              {(summary?.damages ?? 0) === 0 ? (
                                <Badge variant="outline" className="text-muted-foreground">
                                  Inga scenarion
                                </Badge>
                              ) : (
                                <StatusBadges error={summary?.errors ?? 0} warning={summary?.warnings ?? 0} />
                              )}
                            </TableCell>
                            <TableCell>{format(new Date(estimateFeedback.createdAt), 'yyyy-MM-dd HH:mm')}</TableCell>
                            <TableCell>{format(new Date(estimateFeedback.updatedAt), 'yyyy-MM-dd HH:mm')}</TableCell>
                          </DataTableRow>
                        )
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground">
                          {params.search || params.workshop ? 'Inga ärenden matchade sökningen' : 'Inga ärenden hittades'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <ListPagination
                  page={params.page}
                  pageSize={feedbacks?.pageSize ?? 100}
                  total={feedbacks?.total ?? 0}
                  itemLabel="ärenden"
                  onPageChange={page => update({ page: String(page) })}
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}

export default EstimateFeedbacks
